import { shareUnchanged, trackProcessChanges } from './workbookProcessChanges';
import { useWorkbookQueries } from './useWorkbookQueries';
import { restoredEditorState } from './workbookEditorState';
import { useBlocker } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { WorkbookDetail } from '@erp/rpc';
import type {
  WorkbookDefinition,
  WorkbookDiagnostic,
  WorkbookRunMode,
  WorkbookColumnarSheet,
} from '@erp/shared-types';
import { definitionSourceColumns, planWorkbook } from '@erp/workbooks';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import {
  useWorkbookAgentTools,
  type WorkbookAgentPort,
  type WorkbookAgentWorkspace,
} from './useWorkbookAgentTools';
import { useGT } from 'gt-react';
import { useWorkbookEdits } from './workbookEdits';
import { useWorkbookPlan } from './useWorkbookPlan';
import { useUndoTree } from '@/lib/useUndoTree';
import { describeDefinitionChange, sameDefinition } from './workbookHistory';
import { useWorkbookHistoryStep } from './useWorkbookHistoryStep';
import { useWorkbookHistoryNotice } from './useWorkbookHistoryNotice';
import { loadWorkbookHistory, saveWorkbookHistory } from './workbookSession';

/** Puts a Workbook the server just saved in the cache, and refreshes the Workbooks list. */
function cacheSavedWorkbook(
  queryClient: QueryClient,
  workbookId: string,
  workbook: WorkbookDetail,
) {
  queryClient.setQueryData(
    orpc.workbooks.get.queryOptions({ input: { workbookId } }).queryKey,
    workbook,
  );
  void queryClient.invalidateQueries({
    queryKey: orpc.workbooks.list.queryOptions({ input: {} }).queryKey,
  });
}

type AgentFollow = (id: string | null, removed?: readonly string[]) => void;

// fallow-ignore-next-line complexity -- pre-existing 29-hook editor; the agent-follow callback adds one ref.
export function useWorkbookEditor(initial: WorkbookDetail) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const [definition, setDefinition] = useState(initial.definition);
  const [revision, setRevision] = useState(initial.revision);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editVersion, setEditVersion] = useState(0);
  const current = useRef({
    definition: initial.definition,
    revision: initial.revision,
    editVersion: 0,
    savedEditVersion: 0,
  });
  // The saved Definition's Diagnostics, as the server plans them on save;
  // planned the same way for the loaded Definition until the first save.
  const [loadedDiagnostics] = useState(
    () =>
      planWorkbook(
        initial.definition,
        definitionSourceColumns(initial.definition),
      ).diagnostics,
  );
  const savedDiagnostics = useRef<readonly WorkbookDiagnostic[] | null>(null);
  const pending = useRef<Promise<unknown>>(Promise.resolve());
  // How the page shows the tab an agent edit changed; the page sets it (see useWorkbookFollow).
  const followAgent = useRef<AgentFollow | null>(null);
  const setAgentFollow = (follow: AgentFollow) => {
    followAgent.current = follow;
  };
  const workbookId = initial.id;
  const { runs, versions, catalog } = useWorkbookQueries(
    workbookId,
    definition,
  );
  const { plan, planOf } = useWorkbookPlan(workbookId, definition);

  const refetchVersions = versions.refetch;
  /** Shows `next` as an unsaved edit; autosave then saves it. */
  function applyEdit(next: WorkbookDefinition) {
    current.current.definition = next;
    current.current.editVersion++;
    setDefinition(next);
    setEditVersion(current.current.editVersion);
    setDirty(true);
    setError(null);
  }
  // Undo, redo and jumps apply a state as an edit, saved like any other, so
  // the server stays the source of truth and conflicts surface as they do.
  const [restoredHistory] = useState(() =>
    loadWorkbookHistory(initial.id, initial.revision, initial.definition),
  );
  const history = useUndoTree({
    restored: restoredHistory,
    value: definition,
    apply: applyEdit,
    equals: sameDefinition,
    openedLabel: gt('Opened', { context: 'workbook edit history first step' }),
    elsewhereLabel: gt('Changed elsewhere', {
      context: 'workbook edit history step made outside the workbook editor',
    }),
  });
  useWorkbookHistoryNotice(initial.id);
  const stepFor = useWorkbookHistoryStep();
  // Kept for a reload of this tab, with the Revision it was saved at.
  const latestTree = useRef(history.tree);
  useEffect(() => {
    latestTree.current = history.tree;
  }, [history.tree]);
  useEffect(() => {
    const keep = () =>
      saveWorkbookHistory(
        initial.id,
        current.current.revision,
        latestTree.current,
      );
    window.addEventListener('pagehide', keep);
    return () => window.removeEventListener('pagehide', keep);
  }, [initial.id]);
  // The one write path. Panels reach it through `edits`, the agent through its port.
  function commit(
    update: (definition: WorkbookDefinition) => WorkbookDefinition,
    appliedStepIds: readonly string[] = [],
    restoredStepIds: readonly string[] = [],
  ) {
    const previous = current.current.definition;
    const next = trackProcessChanges(
      previous,
      shareUnchanged(previous, update(previous)),
      appliedStepIds,
      restoredStepIds,
    );
    if (next === previous) return;
    history.record(next, {
      ...stepFor(describeDefinitionChange(previous, next)),
      relabel: (base) => stepFor(describeDefinitionChange(base, next)).label,
    });
    applyEdit(next);
  }
  const save = useCallback(
    (checkpoint = false): Promise<void> => {
      const task = pending.current
        .catch(() => undefined)
        .then(async () => {
          const snapshot = { ...current.current };
          if (!checkpoint && snapshot.editVersion === snapshot.savedEditVersion)
            return;
          setSaving(true);
          try {
            const { diagnostics, ...result } = await rpcClient.workbooks.save({
              workbookId,
              definition: snapshot.definition,
              expectedRevision: snapshot.revision,
              checkpoint,
            });
            savedDiagnostics.current = diagnostics;
            cacheSavedWorkbook(queryClient, workbookId, result);
            current.current.revision = result.revision;
            current.current.savedEditVersion = snapshot.editVersion;
            setRevision(result.revision);
            setDirty(current.current.editVersion !== snapshot.editVersion);
            setError(null);
            if (checkpoint) await refetchVersions();
          } catch (cause) {
            setSaving(false);
            setError(
              saveErrorMessage(
                cause,
                gt('Could not save workbook.', {
                  context: 'workbook save error',
                }),
              ),
            );
            throw cause;
          }
          setSaving(false);
        });
      pending.current = task;
      return task;
    },
    [workbookId, refetchVersions, gt, queryClient],
  );
  useBlocker({
    shouldBlockFn: async () => {
      try {
        await save();
        return false;
      } catch {
        return true;
      }
    },
    enableBeforeUnload: () =>
      current.current.editVersion !== current.current.savedEditVersion,
  });
  useEffect(() => {
    if (!dirty) return;
    const timer = window.setTimeout(() => {
      void save().catch(() => undefined);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [editVersion, dirty, save]);
  /** Saves, then starts a Run of the saved Revision in `mode` on the Target (a Worksheet or an Output). */
  async function run(
    mode: WorkbookRunMode = 'entire',
    targetId: string | null = null,
  ) {
    await save();
    const result = await rpcClient.workbooks.run({
      workbookId,
      expectedRevision: current.current.revision,
      mode,
      targetId,
    });
    await runs.refetch();
    return result;
  }
  /** Cancels a Run the editor started; the Run list then shows it failed as cancelled. */
  async function cancelRun(runId: string) {
    const result = await rpcClient.workbooks.cancelRun({ workbookId, runId });
    await runs.refetch();
    return result;
  }
  function restoredLabel(versionId: string) {
    const version = versions.data?.find((entry) => entry.id === versionId);
    return version
      ? gt('Restored version {revision}', {
          revision: String(version.revision),
          context: 'workbook edit history step',
        })
      : gt('Restored a saved version', {
          context: 'workbook edit history step',
        });
  }
  async function restore(versionId: string) {
    await save();
    const task = pending.current.then(async () => {
      const editAtStart = current.current.editVersion;
      const { diagnostics, ...result } = await rpcClient.workbooks.restore({
        workbookId,
        versionId,
        expectedRevision: current.current.revision,
      });
      savedDiagnostics.current = diagnostics;
      cacheSavedWorkbook(queryClient, workbookId, result);
      const before = current.current.definition;
      current.current = restoredEditorState(
        current.current,
        result,
        editAtStart,
      );
      if (current.current.definition !== before)
        history.record(current.current.definition, {
          label: restoredLabel(versionId),
        });
      setRevision(current.current.revision);
      setDefinition(current.current.definition);
      setDirty(
        current.current.editVersion !== current.current.savedEditVersion,
      );
      setEditVersion(current.current.editVersion);
      await refetchVersions();
    });
    pending.current = task;
    await task;
  }
  const agentPort: WorkbookAgentPort = {
    snapshot: () => ({
      definition: current.current.definition,
      token: current.current.editVersion,
      revision: current.current.revision,
    }),
    applyIfUnchanged(token, next, appliedStepIds) {
      if (token !== current.current.editVersion) return false;
      commit(() => next, appliedStepIds);
      return true;
    },
    follow: (id, removed) => followAgent.current?.(id, removed),
    save: async () => {
      await save();
      return savedDiagnostics.current ?? loadedDiagnostics;
    },
    diagnostics: () => plan.diagnostics,
    plan: planOf,
    run,
  };
  // The page around the editor hands its tab and tools to the same registration.
  const [agentWorkspace, setAgentWorkspace] =
    useState<WorkbookAgentWorkspace | null>(null);
  useWorkbookAgentTools({
    workbookId,
    port: agentPort,
    workspace: agentWorkspace,
  });
  const edits = useWorkbookEdits((update, marks) =>
    commit(update, marks?.applied, marks?.restored),
  );
  return {
    definition,
    plan,
    edits,
    revision,
    dirty,
    saving,
    error,
    setError,
    save,
    run,
    cancelRun,
    restore,
    history,
    runs,
    versions,
    catalog,
    workbookId,
    setAgentWorkspace,
    setAgentFollow,
  };
}
export type WorkbookEditor = ReturnType<typeof useWorkbookEditor>;
/** Worksheet writes resolve the sheet by id against the latest definition. */
/** An update to a columnar Worksheet, which the columnar editor writes through. */
export type SheetUpdate = (
  sheet: WorkbookColumnarSheet,
) => WorkbookColumnarSheet;
export type ChangeSheet = (update: SheetUpdate) => void;

function saveErrorMessage(cause: unknown, fallback: string): string {
  return cause instanceof Error ? cause.message : fallback;
}
